import { useState, useCallback } from 'react';
import { produce, Draft } from 'immer';
import { useAuth } from '../lib/auth-context';
import { 
  Flow, 
  Phase, 
  Step, 
  Block, 
  ProcessingResultData,
} from '../types/Document';
import {
  getDocumentProcessingResults,
  getJobResult,
  updateDocumentProcessingResults,
} from '../lib/api-client';
import {
  DEFAULT_PHASE,
  DEFAULT_STEP,
  DEFAULT_BLOCK,
  parsePath,
  navigateToPath,
  navigateToParentCollectionInfo,
} from '../lib/document-utils';

// Helper to deep clone using immer for initial state setup
function deepClone<T>(data: T): T {
  return produce(data, () => {});
}

export interface UseProcessingResultsReturn {
  processingResults: ProcessingResultData;
  editableResults: ProcessingResultData;
  loadingResults: boolean;
  editMode: boolean;
  savingEdits: boolean;
  message: string;
  error: string;
  processingWarnings: string[];
  collapsedPhases: Set<string>;
  openMenuPath: string | null;
  fetchProcessingResults: (documentId: string) => Promise<void>;
  setProcessingResultsState: (results: ProcessingResultData) => void;
  setProcessingWarningsState: (warnings: string[]) => void;
  setEditableResultsState: (results: ProcessingResultData) => void;
  toggleEditMode: () => void;
  cancelEditing: () => void;
  saveEditedResults: (documentId: string) => Promise<void>;
  handleInputChange: (
    path: (string | number)[],
    value: string,
    type?: 'flow' | 'phase' | 'step' | 'block'
  ) => void;
  togglePhaseCollapse: (flowIndex: number, phaseIndex: number) => void;
  setOpenMenuPath: React.Dispatch<React.SetStateAction<string | null>>;
  handleDeleteItem: (pathString: string) => void;
  handleAddItem: (parentPathString: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  handleChangeItemType: (pathString: string, newType: 'phase' | 'step') => void;
  handleCreateStepFromSelection: (selection: string, blockPath: string) => void;
  clearMessages: () => void;
  isFlowArray: boolean;
  hasStructuredResults: boolean;
}

export const useProcessingResults = (): UseProcessingResultsReturn => {
  const { user } = useAuth(); // Needed for saveEditedResults
  const [processingResults, setProcessingResultsState] = useState<ProcessingResultData>(null);
  const [editableResults, setEditableResultsState] = useState<ProcessingResultData>(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set());
  const [openMenuPath, setOpenMenuPath] = useState<string | null>(null);
  const [processingWarnings, setProcessingWarningsState] = useState<string[]>([]);

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  // Helper function to process data in chunks to avoid blocking the main thread
  const processDataInChunks = useCallback(async <T>(data: T): Promise<T> => {
    return new Promise((resolve) => {
      // For large datasets, break processing into chunks
      if (Array.isArray(data) && data.length > 50) {
        const processedData: unknown[] = [];
        let currentIndex = 0;
        
        const processChunk = () => {
          const chunkSize = 10; // Process 10 items at a time
          const endIndex = Math.min(currentIndex + chunkSize, data.length);
          
          // Process current chunk
          for (let i = currentIndex; i < endIndex; i++) {
            processedData.push(data[i]);
          }
          
          currentIndex = endIndex;
          
          if (currentIndex < data.length) {
            // Schedule next chunk processing
            setTimeout(processChunk, 0);
          } else {
            resolve(processedData as T);
          }
        };
        
        processChunk();
      } else {
        // For smaller datasets, process immediately
        resolve(data);
      }
    });
  }, []);

  const fetchProcessingResults = useCallback(async (documentId: string) => {
    if (!user?.token) return;
    setLoadingResults(true);
    try {
      const results = await getDocumentProcessingResults(documentId);
      // Extract warnings if present in the MongoDB response
      if (results && Array.isArray(results.processingWarnings) && results.processingWarnings.length > 0) {
        setProcessingWarningsState(results.processingWarnings);
      } else if (results && results.processingJobId && results.processingStatus === 'done') {
        // Warnings not in MongoDB — try fetching from the processing API directly.
        // This handles documents processed before warnings support was added.
        try {
          const jobResponse = await getJobResult(results.processingJobId);
          if (Array.isArray(jobResponse.warnings) && jobResponse.warnings.length > 0) {
            setProcessingWarningsState(jobResponse.warnings);
            // Persist to MongoDB so we don't have to re-fetch next time
            updateDocumentProcessingResults(documentId, {
              status: 'done',
              warnings: jobResponse.warnings,
            }).catch(err => console.warn('Non-critical: Failed to persist warnings to DB:', err));
          } else {
            setProcessingWarningsState([]);
          }
        } catch {
          // Job result no longer available from processing API — that's fine
          setProcessingWarningsState([]);
        }
      } else {
        setProcessingWarningsState([]);
      }

      let dataToSet = results;
      if (results && results.processingResult) {
        dataToSet = results.processingResult;
        if (typeof results.processingResult === 'string') {
          try { dataToSet = JSON.parse(results.processingResult); } 
          catch (parseErr) { console.error('Error parsing processingResult string:', parseErr); }
        }
      } else if (typeof results === 'string') {
        try { dataToSet = JSON.parse(results); } 
        catch (parseErr) { console.error('Error parsing results string:', parseErr); }
      }
      
      // Process data in chunks to avoid blocking the main thread
      const processedData = await processDataInChunks(dataToSet);
      
      setProcessingResultsState(processedData);
      if (editMode) {
        setEditableResultsState(deepClone(processedData));
      }
    } catch (err) {
      console.error('Error fetching processing results in hook:', err);
      setError(err instanceof Error ? err.message : 'Error fetching results');
    } finally {
      setLoadingResults(false);
    }
  }, [user?.token, editMode, processDataInChunks]); // editMode dependency for editableResults update

  const toggleEditMode = useCallback(() => {
    if (!editMode) {
      setEditableResultsState(deepClone(processingResults));
    }
    setEditMode(!editMode);
    if (editMode) setOpenMenuPath(null); // Close menu when exiting edit mode
  }, [editMode, processingResults]);

  const cancelEditing = useCallback(() => {
    setEditMode(false);
    setEditableResultsState(null);
    setOpenMenuPath(null);
  }, []);

  const saveEditedResults = useCallback(async (documentId: string) => {
    if (!documentId || !user?.token || !editableResults) return;
    setSavingEdits(true);
    setError('');
    try {
      await updateDocumentProcessingResults(documentId, {
        status: 'done',
        result: editableResults,
      });
      // Update the base results to match the saved edits
      setProcessingResultsState(editableResults);
      setMessage('Changes saved successfully');
      setTimeout(() => setMessage(''), 3000);
      // Stay in edit mode - don't change editMode
    } catch (err) {
      console.error('Error saving edited results:', err);
      setError(err instanceof Error ? err.message : 'Error saving edited results');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSavingEdits(false);
    }
  }, [user?.token, editableResults]);

  const handleInputChange = useCallback((
    path: (string | number)[],
    value: string,
    type: 'flow' | 'phase' | 'step' | 'block' = 'block'
  ) => {
    if (!editableResults) return;
    
    const newResults = produce(editableResults, (draft: Draft<ProcessingResultData>) => {
      if (type === 'flow') {
        const flowIndex = path[0] as number;
        const property = path[2] as string;
        const flows = draft as Draft<Flow[]>;
        if (flows[flowIndex]?.Flow && (property === 'name' || property === 'description')) {
          (flows[flowIndex].Flow as Record<string, string>)[property] = value;
        }
      } else {
        let target: unknown = draft;
        for (let i = 0; i < path.length - 1; i++) {
          if ((target as Record<string | number, unknown>)[path[i]] === undefined) return;
          target = (target as Record<string | number, unknown>)[path[i]];
        }
        const propertyToUpdate = path[path.length - 1];
        if (typeof target === 'object' && target !== null && propertyToUpdate in (target as object)) {
          if ((propertyToUpdate === 'name' && (type === 'phase' || type === 'step')) || 
              (propertyToUpdate === 'text' && type === 'block')) {
            (target as Record<string | number, string>)[propertyToUpdate] = value;
          }
        }
      }
    });
    
    setEditableResultsState(newResults);
  }, [editableResults]);

  const togglePhaseCollapse = useCallback((flowIndex: number, phaseIndex: number) => {
    const phaseKey = `${flowIndex}-${phaseIndex}`;
    setCollapsedPhases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phaseKey)) newSet.delete(phaseKey);
      else newSet.add(phaseKey);
      return newSet;
    });
  }, []);

  const handleDeleteItem = useCallback((pathString: string) => {
    if (!editableResults) return;
    const parsedPath = parsePath(pathString);
    if (!parsedPath) return;
    const itemType = parsedPath.type;
    
    try {
      const newResults = produce(editableResults, (draft) => {
        const { collection: parentCollection, indexOrKey } = navigateToParentCollectionInfo(draft, parsedPath);
        if (!parentCollection || indexOrKey === null) return;
        
        if (itemType === 'phase' || itemType === 'step') {
          if (Array.isArray(parentCollection) && typeof indexOrKey === 'number') {
            parentCollection.splice(indexOrKey, 1);
          }
        } else if (itemType === 'block') {
          if (typeof parentCollection === 'object' && parentCollection !== null) {
            delete (parentCollection as Record<string, unknown>)["instruction"];
          }
        }
      });
      
      setEditableResultsState(newResults);
      setOpenMenuPath(null);
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  }, [editableResults]);

  const handleAddItem = useCallback((parentPathString: string, itemTypeToAdd: 'phase' | 'step' | 'block') => {
    if (!editableResults) return;
    const parentParsedPath = parsePath(parentPathString);
    if (!parentParsedPath) return;
    
    try {
      const newResults = produce(editableResults, (draft) => {
        const parentItem = navigateToPath(draft, parentParsedPath);
        if (!parentItem) return;
        
        let newItem: Phase | Step | Block | undefined;
        let targetCollection: unknown[] | undefined;
        let insertionIndex = -1;
        
        if (itemTypeToAdd === 'phase') {
          if (parentParsedPath.type !== 'flow') return;
          newItem = { ...DEFAULT_PHASE };
          targetCollection = parentItem.phases;
          insertionIndex = 0;
        } else if (itemTypeToAdd === 'step') {
          newItem = { ...DEFAULT_STEP };
          if (parentParsedPath.type === 'phase') {
            if (!Array.isArray(parentItem.steps)) return;
            targetCollection = parentItem.steps;
            insertionIndex = 0;
          } else if (parentParsedPath.type === 'step') {
            const [flowIndex, phaseIndex, stepIndex] = parentParsedPath.indices;
            const flows = draft as Flow[];
            const phase = flows[flowIndex]?.phases?.[phaseIndex];
            if (!phase || !Array.isArray(phase.steps)) return;
            targetCollection = phase.steps;
            insertionIndex = stepIndex + 1;
          } else return;
        } else if (itemTypeToAdd === 'block') {
          if (parentParsedPath.type !== 'step') return;
          newItem = { ...DEFAULT_BLOCK };
          if (typeof parentItem.blocks !== 'object' || parentItem.blocks === null) return;
          parentItem.blocks["instruction"] = newItem;
          return; // Early return since block was directly set
        } else return;
        
        if ((itemTypeToAdd === 'phase' || itemTypeToAdd === 'step') && newItem) {
          if (!Array.isArray(targetCollection)) return;
          if (insertionIndex === -1) insertionIndex = targetCollection.length;
          targetCollection.splice(insertionIndex, 0, newItem);
        }
      });
      
      setEditableResultsState(newResults);
      setOpenMenuPath(null);
    } catch (error) {
      console.error("Error adding item:", error);
    }
  }, [editableResults]);

  const handleChangeItemType = useCallback((pathString: string, newType: 'phase' | 'step') => {
    if (!editableResults) return;
    const parsedPath = parsePath(pathString);
    if (!parsedPath) return;
    
    try {
      const newResults = produce(editableResults, (draft) => {
        const item = navigateToPath(draft, parsedPath);
        const { collection: parentCollection, indexOrKey } = navigateToParentCollectionInfo(draft, parsedPath);
        if (!parentCollection || indexOrKey === null || !item) return;
        
        const flows = draft as Flow[];
        
        if (parsedPath.type === 'step' && newType === 'phase') {
          const stepToConvert = item as Step;
          if (!Array.isArray(parentCollection) || typeof indexOrKey !== 'number') return;
          const phaseIndex = parsedPath.indices[1];
          const flowIndex = parsedPath.indices[0];
          const parentPhase = flows[flowIndex]?.phases?.[phaseIndex];
          if (!parentPhase || !Array.isArray(parentPhase.steps)) return;
          const stepIndex = indexOrKey;
          
          // Get the converted step and all subsequent steps
          const stepsForNewPhase = parentPhase.steps.slice(stepIndex);
          
          // Create new phase with step's name and include converted step + subsequent steps
          const newPhase: Phase = { 
            name: stepToConvert.name, 
            description: '', 
            steps: stepsForNewPhase 
          };
          
          // Remove converted step and all subsequent steps from original phase
          parentPhase.steps = parentPhase.steps.slice(0, stepIndex);
          
          // If original phase is now empty, add a placeholder step
          if (parentPhase.steps.length === 0) {
            parentPhase.steps.push({ 
              name: 'Nouvelle étape', 
              blocks: { instruction: { text: '' } } 
            });
          }
          
          // Insert the new phase after the current phase
          const flow = flows[flowIndex];
          if (!flow || !Array.isArray(flow.phases)) return;
          flow.phases.splice(phaseIndex + 1, 0, newPhase);
        } else if (parsedPath.type === 'phase' && newType === 'step') {
          const phaseToConvert = item as Phase;
          if (!Array.isArray(parentCollection) || typeof indexOrKey !== 'number') return;
          
          const phaseIndex = indexOrKey;
          const flowIndex = parsedPath.indices[0];
          const flow = flows[flowIndex];
          if (!flow || !Array.isArray(flow.phases)) return;
          
          // Create the converted step from the phase
          // Use the phase's description as the instruction block text
          const convertedStep: Step = { 
            name: phaseToConvert.name, 
            blocks: { 
              instruction: { 
                text: phaseToConvert.description || '' 
              } 
            } 
          };
          
          // All steps to move: the converted step first, then existing steps from the phase
          const stepsToMove = [convertedStep, ...phaseToConvert.steps];
          
          if (phaseIndex === 0) {
            // No previous phase - create a placeholder phase
            const placeholderPhase: Phase = { 
              name: 'Phase title', 
              description: '', 
              steps: stepsToMove 
            };
            // Replace the phase being converted with the placeholder
            flow.phases.splice(phaseIndex, 1, placeholderPhase);
          } else {
            // Has previous phase - add steps to the previous phase
            const previousPhase = flow.phases[phaseIndex - 1];
            if (!previousPhase || !Array.isArray(previousPhase.steps)) return;
            
            // Add all steps to the previous phase
            previousPhase.steps.push(...stepsToMove);
            
            // Remove the converted phase
            flow.phases.splice(phaseIndex, 1);
          }
        } else if (parsedPath.type === 'block' && newType === 'step') {
          if (typeof parentCollection !== 'object' || parentCollection === null) return;
          const blockToConvert = item as Block;
          delete (parentCollection as Record<string, unknown>)["instruction"];
          (parentCollection as Record<string, Block>)["instruction"] = { text: "Enter new instruction here..." };
          const newStep: Step = { name: blockToConvert.text, blocks: { instruction: { text: "Enter instruction here..." } } };
          const phase = flows[parsedPath.indices[0]]?.phases?.[parsedPath.indices[1]];
          if (!phase || !Array.isArray(phase.steps)) return;
          phase.steps.splice(parsedPath.indices[2] + 1, 0, newStep);
        }
      });
      
      setEditableResultsState(newResults);
      setOpenMenuPath(null);
    } catch (error) {
      console.error("Error changing item type:", error);
    }
  }, [editableResults]);

  const handleCreateStepFromSelection = useCallback((selection: string, blockPath: string) => {
    if (!editableResults || !selection.trim()) return;
    const parsedPath = parsePath(blockPath);
    if (!parsedPath || parsedPath.type !== 'block') return;
    
    const newResults = produce(editableResults, (draft) => {
      const [flowIndex, phaseIndex, stepIndex] = parsedPath.indices;
      const flows = draft as Flow[];
      const step = flows[flowIndex]?.phases?.[phaseIndex]?.steps?.[stepIndex];
      if (!step?.blocks?.instruction?.text) return;
      
      const originalText = step.blocks.instruction.text;
      const selectionStart = originalText.indexOf(selection);
      if (selectionStart === -1) return;
      
      step.blocks.instruction.text = (originalText.slice(0, selectionStart) + originalText.slice(selectionStart + selection.length)).trim();
      const newStepToAdd: Step = { name: "New Step", blocks: { instruction: { text: selection.trim() } } };
      const phase = flows[flowIndex]?.phases?.[phaseIndex];
      if (!phase || !Array.isArray(phase.steps)) return;
      phase.steps.splice(stepIndex + 1, 0, newStepToAdd);
    });
    
    setEditableResultsState(newResults);
  }, [editableResults]);

  const isFlowArray = Array.isArray(processingResults) && processingResults.length > 0 && processingResults[0]?.Flow !== undefined;
  const hasStructuredResults = processingResults && typeof processingResults === 'object' && !Array.isArray(processingResults);

  return {
    processingResults,
    editableResults,
    loadingResults,
    editMode,
    savingEdits,
    message,
    error,
    processingWarnings,
    collapsedPhases,
    openMenuPath,
    fetchProcessingResults,
    setProcessingResultsState, // Expose setter
    setProcessingWarningsState, // Expose setter
    setEditableResultsState, // Expose setter
    toggleEditMode,
    cancelEditing,
    saveEditedResults,
    handleInputChange,
    togglePhaseCollapse,
    setOpenMenuPath,
    handleDeleteItem,
    handleAddItem,
    handleChangeItemType,
    handleCreateStepFromSelection,
    clearMessages,
    isFlowArray,
    hasStructuredResults,
  };
}; 